import './index.css'
import { useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment/moment'
import { PageLoadingSpinner } from 'ui_kit_qmd'
import { findIana } from 'windows-iana'
import { clear500Error, clearInternalError, logout, LOGOUT, setConnection } from './app/authState'
import Visits from './pages/visits/Visits'
import Management from './pages/management/management'
import { store } from './app/store'
import NewService from './pages/new-service/newservice'
import ViewService from './pages/view-service/viewservice'
import { ForgotPassword } from './pages/forgot-password/forgotpassword'
import ResetPassword from './pages/reset-password/resetpassword'
import NewMembers from './pages/new-members/new-members'
import Member from './pages/member/member'
import Home from './pages/home/home'
import { AuthenticatedLayout } from './components/authenticated-layout/AuthenticatedLayout'
import AccountSettings from './pages/accountSettings/account-settings'
import CreateNewIntakeForm from './pages/management/IntakeForms/CreateNewIntakeForm/CreateNewIntakeForm'
import {
  AVAILABILITY_LABEL,
  AVAILABILITY_ROUTE,
  AUTH_VALIDATE_ROUTE,
  MEDICATIONS_PAGE_ROUTE,
  MEDICATIONS_ADD_ROUTE,
  MEDICATIONS_EDIT_ROUTE,
  LICENSE_ADD_ROUTE,
  LICENSE_EDIT_ROUTE,
  CREATE_NEW_SERVICE_TYPE_ROUTE,
  EDIT_STATE_OPTIONS_PAGE_ROUTE,
  FORGOT_PASSWORD_PAGE_PATH,
  HOME_PAGE_ROUTE,
  SANDBOX_PAGE_ROUTE,
  MANAGEMENT_LABEL,
  MANAGEMENT_PAGE_ROUTE,
  MERGE_PATIENT_ACCOUNTS_ROUTE,
  NAVIGATION_TYPES,
  NEW_INTAKE_FORM_ROUTE,
  NEW_VISIT_MANAGEMENT_ROUTE,
  NEW_VISIT_ROUTE,
  NEW_VISIT_VISITS_ROUTE,
  PATIENT_CHART_INTAKE_QUESTIONS_PAGE_PATH,
  PATIENT_CHART_PAGE_ROUTE,
  PATIENT_CHARTS_PAGE_ROUTE,
  PATIENT_PAST_VISIT_ROUTE,
  PATIENT_UPCOMING_VISIT_DETAILS_ROUTE,
  PRINTED_FRIENDLY_PATIENT_CHARTS,
  RESCHEDULE_VISIT_PAGE_PATH,
  RESET_PASSWORD_PAGE_PATH,
  SESSION_ON_STORAGE_KEY,
  VERIFY_EMAIL_PAGE_PATH,
  VIEW_INTAKE_FORM_ROUTE,
  VISITS_LABEL,
  VISITS_PAGE_ROUTE,
} from './utils/constants'
import { AuthValidate } from './pages/auth-validate/AuthValidate'
import ViewIntakeForm from './pages/management/IntakeForms/ViewIntakeForm/ViewIntakeForm'
import AvailabilityPage from './pages/Availability/AvailabilityPage'
import CalendarPage from './pages/CalendarPage/CalendarPage'
import { MergeAccountsPage } from '@/pages/merge-accounts/MergeAccountsPage'
import { MedicationsPage } from '@/pages/medications/MedicationsPage'
import { AddMedicationPage } from '@/pages/medications/AddMedicationPage'
import { EditMedicationPage } from '@/pages/medications/EditMedicationPage'
import { AddLicensePage } from '@/pages/member/license-form/AddLicensePage'
import { EditLicensePage } from '@/pages/member/license-form/EditLicensePage'
import { PatientChartsPage } from './pages/patient-charts/PatientChartsPage'
import { PatientChartPage } from './pages/patient-chart-page/PatientChartPage'
import { VisitInfo } from './pages/visit-info/VisitInfo'
import { NewVisit } from './pages/new-visit/NewVisit'
import { getPaymentKeys } from './store/new-visit/NewVisitThunk'
import PatientChartsPrintedFriendly from './pages/patientChartsPrintedFriendly/PatientChartsPrintedFriendly'
import IntakeFormsViewMode from './pages/patient-chart-page/components/intake-forms/intakeFormsViewMode/intakeFormsViewMode'
import { appInsights } from './utils/appInsights'
import { setTabId } from './utils/common'
import CustomTabTitle from './components/customTabTitle/CustomTabTitle'
import { dateHelper } from './utils/dateUtils'
import { shouldLogOutForInactivity, shouldLogOutOnBoot } from './utils/bootSessionGuards'
import { getDrugRulesSettings } from './store/drugRulesSettings/drugRulesSettingsThunk'
import { setInitialTimeZone, setTimeZoneIsInited } from './store/timeZone/timeZoneActions'
import FilePopUpContainer from './components/filePopUpContainer/FilePopUpContainer'
import EditStateSettings from './pages/edit-state-settings/EditStateSettings'
import IDVerificationPopUpContainer from './components/id-verification-pop-up/IDVerificationPopUpContainer'
import { NewVisitReschedule } from './pages/new-visit/NewVisitReschedule'
import { ErrorBoundary } from '@sentry/react'
import { WebAppErrorShell, toast, ToastContainer } from '@quickmd-llc/design-system'
import { DevToolsShell, DevToolsAppContent } from './features/devTools/components/DevTools'
import { isDevToolsEnabled } from './features/devTools/isDevToolsEnabled'
import { SandboxPage } from './pages/Sandbox/SandboxPage'
import env from './env.json'
import { StytchHeadlessClient } from '@stytch/vanilla-js/dist/index.headless'
import { StytchProvider } from '@stytch/react'
import { getFlags } from './store/flags/FlagsActions'

const SESSION_STORAGE_TIMEOUT_KEY = 'sessionWillTimeout'
const SESSION_STORAGE_TIMEOUT_LENGTH = 10

appInsights.addTelemetryInitializer((t) => t.baseType === 'ExceptionData' || t.baseType === 'EventData')

const HIPAASecurePages = [
  {
    path: VISITS_PAGE_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='Visits'>
        <Visits />
      </CustomTabTitle>
    ),
  },
  {
    path: MANAGEMENT_PAGE_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='Clinic Management'>
        <Management />
      </CustomTabTitle>
    ),
  },
  {
    path: EDIT_STATE_OPTIONS_PAGE_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='Edit State Settings'>
        <EditStateSettings />
      </CustomTabTitle>
    ),
  },
  {
    path: '/accountsettings',
    pageComponent: (
      <CustomTabTitle pageTitle='Account Settings'>
        <AccountSettings />
      </CustomTabTitle>
    ),
  },
  {
    path: AVAILABILITY_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='Availability'>
        <AvailabilityPage />
      </CustomTabTitle>
    ),
  },
  {
    path: CREATE_NEW_SERVICE_TYPE_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='New Service'>
        <NewService />
      </CustomTabTitle>
    ),
  },
  {
    path: '/management/view-service',
    pageComponent: <ViewService />,
  },
  {
    path: '/management/new-members',
    pageComponent: (
      <CustomTabTitle pageTitle='New Member'>
        <NewMembers />
      </CustomTabTitle>
    ),
  },
  {
    path: NEW_INTAKE_FORM_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='Intake Form'>
        <CreateNewIntakeForm />
      </CustomTabTitle>
    ),
  },
  {
    path: VIEW_INTAKE_FORM_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='Intake Form'>
        <ViewIntakeForm />
      </CustomTabTitle>
    ),
  },
  {
    path: PATIENT_CHART_INTAKE_QUESTIONS_PAGE_PATH,
    pageComponent: <IntakeFormsViewMode />,
  },

  {
    path: LICENSE_ADD_ROUTE,
    pageComponent: <AddLicensePage />,
  },
  {
    path: LICENSE_EDIT_ROUTE,
    pageComponent: <EditLicensePage />,
  },
  {
    path: '/management/member',
    pageComponent: <Member />,
  },
  {
    path: '/availability/calendar/:queryId',
    pageComponent: (
      <CustomTabTitle pageTitle='Calendar'>
        {/*
         // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered */}
        <CalendarPage crumbsLabel={AVAILABILITY_LABEL} />
      </CustomTabTitle>
    ),
  },
  {
    path: '/management/calendar/:queryId',
    pageComponent: (
      <CustomTabTitle pageTitle='Calendar'>
        {/*
         // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered */}
        <CalendarPage crumbsLabel={MANAGEMENT_LABEL} />
      </CustomTabTitle>
    ),
  },
  {
    path: '/visits/calendar/:queryId',
    pageComponent: (
      <CustomTabTitle pageTitle='Calendar'>
        {/*
         // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered */}
        <CalendarPage crumbsLabel={VISITS_LABEL} />
      </CustomTabTitle>
    ),
  },
  {
    path: PATIENT_CHARTS_PAGE_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='Patient Charts'>
        <PatientChartsPage />
      </CustomTabTitle>
    ),
  },
  {
    path: PATIENT_CHART_PAGE_ROUTE,
    pageComponent: <PatientChartPage />,
  },
  {
    path: MERGE_PATIENT_ACCOUNTS_ROUTE,
    pageComponent: <MergeAccountsPage />,
  },
  {
    path: `${PATIENT_PAST_VISIT_ROUTE}/:id`,
    pageComponent: <VisitInfo />,
  },
  {
    path: `${PATIENT_UPCOMING_VISIT_DETAILS_ROUTE}/:id`,
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    pageComponent: <VisitInfo isUpcomingVisit />,
  },
  {
    path: NEW_VISIT_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='New Visit'>
        <NewVisit />
      </CustomTabTitle>
    ),
  },
  {
    path: NEW_VISIT_MANAGEMENT_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='New Visit'>
        <NewVisit />
      </CustomTabTitle>
    ),
  },
  {
    path: NEW_VISIT_VISITS_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='New Visit'>
        <NewVisit />
      </CustomTabTitle>
    ),
  },
  {
    path: RESCHEDULE_VISIT_PAGE_PATH,
    pageComponent: (
      <CustomTabTitle pageTitle='Reschedule visit'>
        <NewVisitReschedule />
      </CustomTabTitle>
    ),
  },
  {
    path: `${PRINTED_FRIENDLY_PATIENT_CHARTS}/:id`,
    pageComponent: <PatientChartsPrintedFriendly />,
    hideNav: true,
  },
  {
    path: MEDICATIONS_PAGE_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='Medications'>
        <MedicationsPage />
      </CustomTabTitle>
    ),
  },
  {
    path: MEDICATIONS_ADD_ROUTE,
    pageComponent: (
      <CustomTabTitle pageTitle='Add Medication'>
        <AddMedicationPage />
      </CustomTabTitle>
    ),
  },
  {
    path: `${MEDICATIONS_EDIT_ROUTE}/:id`,
    pageComponent: (
      <CustomTabTitle pageTitle='Edit Medication'>
        <EditMedicationPage />
      </CustomTabTitle>
    ),
  },
]
const pagesWithoutHIPPARequirementsCheck = [
  {
    path: HOME_PAGE_ROUTE,
    pageComponent: <Home />,
  },
  {
    path: AUTH_VALIDATE_ROUTE,
    pageComponent: <AuthValidate />,
  },
  {
    path: VERIFY_EMAIL_PAGE_PATH,
    pageComponent: <ResetPassword type='verifyEmail' />,
  },
  {
    path: FORGOT_PASSWORD_PAGE_PATH,
    pageComponent: (
      <CustomTabTitle pageTitle='Forgot Password'>
        <ForgotPassword />
      </CustomTabTitle>
    ),
  },
  {
    path: RESET_PASSWORD_PAGE_PATH,
    pageComponent: <ResetPassword type='resetPassword' />,
  },
]

const perfEntries = window.performance.getEntriesByType('navigation')
const perfEntry = perfEntries[0]
// @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
const pageAccessedByReloadCheck = perfEntry.type === NAVIGATION_TYPES.RELOAD
// @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
const isPageWasOpenedFromHistory = perfEntry.type === NAVIGATION_TYPES.BACK_FORWARD
const stytch = new StytchHeadlessClient(env.stytchToken)

export default function App() {
  const dispatch = useDispatch()
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const serverErrorMessage = useSelector((state) => state.auth.serverErrorMessage)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const timeZoneFromTheStore = useSelector((state) => state.timeZoneReducer.initialTimeZone)

  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const loggedIn = useSelector((state) => state.auth.loggedIn)
  const defaultServerErrorMessage = useSelector(
    (state) =>
      // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
      state.home.defaultServerErrorMessage ||
      // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
      state.auth.defaultServerErrorMessage ||
      // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
      state.newService.defaultServerErrorMessage ||
      // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
      state.viewService.defaultServerErrorMessage ||
      // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
      state.forgotPassword.defaultServerErrorMessage ||
      // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
      state.newClinicMember.defaultServerErrorMessage ||
      // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
      state.member.defaultServerErrorMessage,
  )

  const [isLogoutLoading, setIsLogoutLoading] = useState(true)

  const logoutFromApp = () => {
    // Clearing here used to be the whole teardown, and it never paused the persistoid, so
    // the next throttle tick wrote the patient list back. `logoutTeardownMiddleware` owns
    // it now, for every logout path rather than this one. See QMD-4332.
    dispatch(logout('manual'))
    setIsLogoutLoading(false)
  }

  useEffect(() => {
    if (loggedIn) {
      dispatch(getPaymentKeys())
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      dispatch(getDrugRulesSettings())
    }
  }, [loggedIn])
  useEffect(() => {
    toast.dismiss('single')
  }, [window.location.pathname])

  useEffect(() => {
    getFlags()
    if (
      window.location.pathname !== '/verifyemail' &&
      window.location.pathname !== '/resetPassword' &&
      window.location.pathname !== '/forgot-password' &&
      window.location.pathname !== AUTH_VALIDATE_ROUTE
    ) {
      setIsLogoutLoading(true)
      const bc = new BroadcastChannel('QuickMd_Admin_Channel')
      const timeInMilliseconds = dateHelper.getTimeInMilliseconds()
      const sessionTimeoutLength = sessionStorage.getItem(SESSION_STORAGE_TIMEOUT_KEY)
      const hasKey = sessionStorage.getItem(SESSION_ON_STORAGE_KEY)

      const logoutInterval = setTimeout(() => {
        const sessionSurvived =
          hasKey === 'true' &&
          !isPageWasOpenedFromHistory &&
          moment().isBefore(moment(sessionTimeoutLength || undefined))
        if (shouldLogOutOnBoot({ sessionSurvived, loggedIn: store.getState().auth.loggedIn })) {
          logoutFromApp()
        } else {
          setIsLogoutLoading(false)
        }
      }, 1000)

      bc.onmessage = (event) => {
        if (event.data === `Is opened?`) {
          const currentTime = dateHelper.getTimeInMilliseconds()
          if (timeInMilliseconds + 150 < currentTime) {
            bc.postMessage(`Yes, already opened.`)
            window.clearTimeout(logoutInterval)
            sessionStorage.setItem(SESSION_ON_STORAGE_KEY, JSON.stringify(true))
            setIsLogoutLoading(false)
          }
        }
        if (event.data === `Yes, already opened.`) {
          setIsLogoutLoading(false)
          window.clearTimeout(logoutInterval)
          sessionStorage.setItem(SESSION_ON_STORAGE_KEY, JSON.stringify(true))
        }
      }
      if (isPageWasOpenedFromHistory) {
        sessionStorage.setItem(SESSION_ON_STORAGE_KEY, JSON.stringify(false))
      }
      if (!pageAccessedByReloadCheck) {
        bc.postMessage(`Is opened?`)
      } else {
        window.clearTimeout(logoutInterval)
        setIsLogoutLoading(false)
      }
    } else {
      setIsLogoutLoading(false)
    }
    const unload = () => {
      sessionStorage.setItem(
        SESSION_STORAGE_TIMEOUT_KEY,
        moment().add(SESSION_STORAGE_TIMEOUT_LENGTH, 'seconds').format(),
      )
    }
    window.addEventListener('beforeunload', unload)

    return () => {
      window.removeEventListener('beforeunload', unload)
    }
  }, [])

  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const isTimeZoneInited = useSelector((state) => state.timeZoneReducer.isInited)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const profile = useSelector((state) => state.accountSettings.adminProfile)
  useEffect(() => {
    if (profile && !isTimeZoneInited && !timeZoneFromTheStore) {
      dispatch(
        setInitialTimeZone(
          profile.TimeZone ? findIana(profile.TimeZone)[0] : Intl.DateTimeFormat().resolvedOptions().timeZone,
        ),
      )
      dispatch(setTimeZoneIsInited(true))
    }
  }, [profile, dispatch, isTimeZoneInited, timeZoneFromTheStore])

  useEffect(() => {
    setTabId()
    // Guarded — see `utils/bootSessionGuards`. Unguarded, this fires on every logged-out
    // load and the teardown's reload lands straight back here.
    if (shouldLogOutForInactivity(localStorage.getItem('lastUserAction'), dateHelper.getTimeInMilliseconds())) {
      dispatch({ type: LOGOUT, reason: 'mount-inactivity-check' })
    }

    function changeStatus() {
      store.dispatch(setConnection(navigator.onLine))
    }

    const resetManagementServerError = () => {
      if (window.location.pathname === '/management') {
        dispatch(clearInternalError())
      }
    }

    window.addEventListener('popstate', resetManagementServerError)
    window.addEventListener('online', changeStatus)
    window.addEventListener('offline', changeStatus)
    dispatch(clearInternalError())
    dispatch(clear500Error())

    store.dispatch(setConnection(navigator.onLine))
    return () => {
      window.removeEventListener('popstate', resetManagementServerError)
      window.removeEventListener('online', changeStatus)
      window.removeEventListener('offline', changeStatus)
      dispatch(clearInternalError())
      dispatch(clear500Error())
    }
  }, [])

  useEffect(() => {
    if (serverErrorMessage) appInsights.trackEvent({ name: 'SERVER ERROR ADMIN', properties: serverErrorMessage })
  }, [serverErrorMessage])
  useEffect(() => {
    if (defaultServerErrorMessage)
      appInsights.trackEvent({ name: 'SERVER ERROR ADMIN', properties: defaultServerErrorMessage })
  }, [defaultServerErrorMessage])

  if (isLogoutLoading) {
    return <PageLoadingSpinner />
  }
  return (
    <WebAppErrorShell
      ErrorBoundary={ErrorBoundary}
      homeHref={loggedIn ? MANAGEMENT_PAGE_ROUTE : HOME_PAGE_ROUTE}
      isDevToolsEnabled={isDevToolsEnabled}
      devTools={<DevToolsShell />}>
      <StytchProvider stytch={stytch}>
        <div>
          {loggedIn ?
            <>
              <FilePopUpContainer />
              <IDVerificationPopUpContainer />
            </>
          : null}
          <BrowserRouter>
            {isDevToolsEnabled ?
              <DevToolsAppContent />
            : null}
            <Routes>
              {pagesWithoutHIPPARequirementsCheck.map(({ path, pageComponent }) => (
                <Route key={path} path={path} element={pageComponent} />
              ))}
              <Route element={<AuthenticatedLayout />}>
                {/*
                 // @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered */}
                {HIPAASecurePages.map(({ path, pageComponent, hideNav }) => (
                  <Route key={path} path={path} element={pageComponent} />
                ))}
              </Route>
              {isDevToolsEnabled ?
                <Route path={SANDBOX_PAGE_ROUTE} element={<SandboxPage />} />
              : null}
              {/*
               // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered */}
              <Route exact path='*' element={<Navigate to={HOME_PAGE_ROUTE} />} />
            </Routes>
          </BrowserRouter>
        </div>
        <ToastContainer limit={1} icon={false} />
      </StytchProvider>
    </WebAppErrorShell>
  )
}
